import { useState, useCallback, useEffect, useRef } from 'react'
import { isElectron, processDroppedPaths, classifyDroppedFiles, type MediaFile } from '@/lib/electron'
import { Upload } from 'lucide-react'

export function DropZone({ onFilesDropped }: { onFilesDropped?: (files: MediaFile[]) => void }) {
  const [dragging, setDragging] = useState(false)
  const dragCounter = useRef(0)

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer?.types.includes('Files')) {
      setDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setDragging(false)

    const files = e.dataTransfer?.files
    if (!files?.length) return

    const paths: string[] = []
    for (let i = 0; i < files.length; i++) {
      const f = files[i] as any
      const filePath = f.path
      if (filePath) paths.push(filePath)
    }

    if (paths.length === 0) return

    const rawFiles = await processDroppedPaths(paths)
    const media = classifyDroppedFiles(rawFiles)
    if (media.length > 0 && onFilesDropped) {
      onFilesDropped(media)
    }
  }, [onFilesDropped])

  useEffect(() => {
    if (!isElectron()) return

    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)

    return () => {
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  if (!isElectron() || !dragging) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none">
      <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-sky-400/60 bg-card/90 backdrop-blur-md px-16 py-12">
        <Upload className="h-10 w-10 text-sky-400" />
        <p className="text-lg font-medium text-foreground">Drop media files here</p>
        <p className="text-sm text-muted-foreground">Videos, images, and audio files</p>
      </div>
    </div>
  )
}
